import Markdown from "../shared/Markdown";
import { FaCheck, FaCopy, FaPlay, FaStop, FaUser } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import React, { useState, useRef, useEffect, useCallback } from "react";
import './ChatLine.css';
import { textToSpeech, getThreadFileUrls } from "../../services/chat.service";
import { useApp } from "../../hooks";
import { styled, useTheme, alpha } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { Avatar } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { Vortex } from "react-loader-spinner";
import datalinker_sm_logo from "../../assets/icons/datalinker_sm_logo.png";
import ReactDOMServer from 'react-dom/server';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CodeIcon from '@mui/icons-material/Code';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import {
    Grid,
    Modal,
    Backdrop,
    CircularProgress,
    Fade
} from '@mui/material';

export type FileDetail = {
    file_id: string;
    file_name: string;
    file_type: string;
    is_google_drive: boolean;
    lastEditedUtc: string;
};

export type Message = {
    who: 'bot' | 'user' | undefined;
    message?: string;
    spents: any;
    messageId: string;
    threadId: string;
    username: string | undefined;
    userId: string | undefined;
    createdAt: string;
    isCompleteMessage?: boolean;
    loading?: boolean;
    isLastMessage?: boolean;
    fileDetails?: FileDetail[];
};

type FilePreviewInfo = {
    fileUrl?: string;
    previewUrl?: string;
    isLoading: boolean;
    error?: string;
};

type PreviewModalState = {
    isOpen: boolean;
    title: string | null;
    type: 'image' | 'iframe' | null;
    src: string | null;
};

export const LoadingChatLine = () => {
    return (
        <div className="mini-loader">
            <Vortex
                visible={true}
                height="30"
                width="30"
                ariaLabel="vortex-loading"
                colors={['#6366f1', '#4f46e5', '#4338ca', '#3730a3', '#312e81', '#1e1b4b']}
            />
        </div>
    );
};

const ChatLineContainer = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'who',
})<{ who: 'bot' | 'user' | undefined }>(({ theme, who }) => ({
    position: 'relative',
    maxWidth: '90%',
    width: 'fit-content',
    minWidth: '300px',
    margin: theme.spacing(1),
    padding: theme.spacing(2.5),
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    border: '1px solid transparent',
    background: 'none',
    boxShadow: 'none',

    ...(who === 'bot' && {
        borderLeft: `2px solid ${theme.palette.mode === 'dark'
            ? theme.palette.primary.dark
            : theme.palette.primary.light}`,
        backgroundColor: 'transparent',
        boxShadow: theme.shadows[0],
        '&:hover': {
            backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.01)',
            transform: 'translateX(2px)'
        }
    }),

    ...(who === 'user' && {
        backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.03)',
        border: `1px solid ${theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.08)'}`,
        borderRight: `2px solid ${theme.palette.secondary.main}`,
        boxShadow: theme.shadows[1],
        '&:hover': {
            backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.07)'
                : 'rgba(0, 0, 0, 0.05)',
            transform: 'translateX(-2px)'
        }
    }),

    [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
        minWidth: 'unset',
        padding: theme.spacing(2)
    }
}));


// --- Modal Styles ---
const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95vw', sm: '90vw', md: '80vw' },
    height: { xs: '80vh', sm: '85vh' },
    bgcolor: 'background.paper',
    border: `1px solid ${alpha('#000', 0.1)}`,
    borderRadius: 1,
    boxShadow: 24,
    p: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
};

const modalHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    py: 1,
    px: 2,
    borderBottom: (theme: any) => `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
};

const modalContentStyle = {
    flexGrow: 1,
    p: 0,
    overflow: 'hidden',
    position: 'relative',
};

const iframeStyle: React.CSSProperties = {
    border: 'none',
    width: '100%',
    height: '100%',
    display: 'block',
};

const imagePreviewWrapperStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '8px',
    boxSizing: 'border-box',
    overflow: 'auto'
};

const imagePreviewStyle: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block',
    userSelect: 'none',
};


// --- ChatLine Component ---

export function ChatLine({
                             who = 'bot',
                             message,
                             messageId,
                             threadId,
                             username,
                             loading = false,
                             isLastMessage,
                             fileDetails = []
                         }: Message) {
    const theme = useTheme();
    const [isCopied, setIsCopied] = useState(false);
    const [isTTSPlaying, setIsTTSPlaying] = useState(false);
    const [isTTSLoading, setIsTTSLoading] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const { state }: any = useApp();

    const [filePreviewData, setFilePreviewData] = useState<Record<string, FilePreviewInfo>>({});
    const [previewModalState, setPreviewModalState] = useState<PreviewModalState>({
        isOpen: false,
        title: null,
        type: null,
        src: null,
    });

    // --- Helper functions ---
    const isImage = useCallback((type: string | undefined): boolean => {
        return type ? ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(type.toLowerCase()) : false;
    }, []);

    const getIconComponent = (type: string | undefined, size: 'small' | 'medium' | 'large' = 'medium') => {
        const fontSize = size;
        switch (type?.toLowerCase()) {
            case 'pdf': return <PictureAsPdfIcon fontSize={fontSize} sx={{ color: '#D32F2F' }} />;
            case 'py': case 'js': case 'ts': case 'java': case 'cpp': case 'html': case 'css': return <CodeIcon fontSize={fontSize} color="info" />;
            case 'doc': case 'docx': return <InsertDriveFileIcon fontSize={fontSize} sx={{ color: '#1976D2' }} />;
            case 'xls': case 'xlsx': return <InsertDriveFileIcon fontSize={fontSize} sx={{ color: '#388E3C' }} />;
            case 'ppt': case 'pptx': return <InsertDriveFileIcon fontSize={fontSize} sx={{ color: '#F57C00' }} />;
            case 'zip': case 'rar': case '7z': return <InsertDriveFileIcon fontSize={fontSize} color="secondary" />;
            case 'png': case 'jpg': case 'jpeg': case 'gif': case 'bmp': case 'webp': return <ImageOutlinedIcon fontSize={fontSize} color="success" />;
            default: return <InsertDriveFileIcon fontSize={fontSize} color="action" />;
        }
    };

    const getLocalApiFileUrl = (file: FileDetail): string => `/api/files/${file.file_id}`;

    // --- Clipboard copy ---
    const copyToClipboard = useCallback(async () => {
        if (!message) return;
        try {
            await navigator.clipboard.writeText(message);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
        catch (err) {
            console.error('Clipboard copy failed:', err);
        }
    }, [message]);

    const playAudio = useCallback(async () => {
        if (!message || !audioRef.current || isTTSLoading || isTTSPlaying) return;
        setIsTTSLoading(true);
        try {
            const organisationId = state.selectedOrganisation?._id;
            if (!organisationId) throw new Error("Organisation ID not found");

            const res: any = await textToSpeech(message, messageId, organisationId);
            console.log('TTS API Response:', res);
            const url = res?.data?.data?.audio_url;

            if (url && audioRef.current) {
                console.log('Audio URL:', url);
                audioRef.current.src = url;

                audioRef.current.onloadeddata = async () => {
                    try {
                        console.log('Audio loaded successfully');
                        await audioRef.current?.play();
                        setIsTTSPlaying(true);
                    } catch (playError) {
                        console.error("TTS play error:", playError);
                        setIsTTSPlaying(false);
                    }
                };

                audioRef.current.onended = () => {
                    console.log('Audio playback ended');
                    setIsTTSPlaying(false);
                };

                audioRef.current.onerror = (e) => {
                    console.error("TTS audio element error:", e);
                    setIsTTSPlaying(false);
                    setIsTTSLoading(false);
                };

                audioRef.current.load();
            } else {
                throw new Error("No audio URL received or audio element missing");
            }
        } catch (err) {
            console.error('TTS error:', err);
            setIsTTSPlaying(false);
        } finally {
            if (!isTTSPlaying) {
                setIsTTSLoading(false);
            }
        }
    }, [message, messageId, state.selectedOrganisation?._id, isTTSLoading, isTTSPlaying]);

    const stopAudio = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsTTSPlaying(false);
            setIsTTSLoading(false);
            audioRef.current.src = '';
            audioRef.current.onloadeddata = null;
            audioRef.current.onended = null;
            audioRef.current.onerror = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            stopAudio();
        };
    }, [stopAudio]);

    const fetchPreviewUrlForFile = useCallback(async (file: FileDetail): Promise<Pick<FilePreviewInfo, 'fileUrl' | 'previewUrl'> | null> => {
        const { file_id, file_type } = file;
        const organisationId = state.selectedOrganisation?._id;

        if (!file_id || !file_type || !threadId || !organisationId) {
            console.warn("Missing parameters for fetching file URLs for file_id:", file_id, { hasFileId: !!file_id, hasFileType: !!file_type, hasThreadId: !!threadId, hasOrgId: !!organisationId });
            setFilePreviewData(prev => ({ ...prev, [file_id]: { isLoading: false, error: "Missing parameters to fetch URL." } }));
            return null;
        }

        const existingData = filePreviewData[file_id];

        if (existingData && (existingData.fileUrl || existingData.previewUrl) && !existingData.error) {
            return { fileUrl: existingData.fileUrl, previewUrl: existingData.previewUrl };
        }

        if (existingData?.isLoading) {
            return null;
        }

        setFilePreviewData(prev => ({ ...prev, [file_id]: { ...(prev[file_id] || { isLoading: false }), isLoading: true, error: undefined } }));
        try {
            const response: any = await getThreadFileUrls(file_id, file_type, threadId, organisationId);
            if (response?.data?.success === 1 && response?.data?.data) {
                const urls = { fileUrl: response.data.data.file_url, previewUrl: response.data.data.preview_url };
                setFilePreviewData(prev => ({ ...prev, [file_id]: { isLoading: false, ...urls, error: undefined } }));
                return urls;
            } else {
                throw new Error(response?.data?.data?.message || "API returned failure while fetching URLs");
            }
        } catch (error: any) {
            console.error(`Error fetching URLs for file ${file_id}:`, error);
            setFilePreviewData(prev => ({ ...prev, [file_id]: { ...(prev[file_id] || { isLoading: false }), isLoading: false, error: error.message || "Failed to load file info" } }));
            throw error;
        }
    }, [threadId, state.selectedOrganisation?._id, filePreviewData, getThreadFileUrls]);


    const handleOpenPreview = useCallback((file: FileDetail, previewInfoFromFetch: Pick<FilePreviewInfo, 'fileUrl' | 'previewUrl'>) => {
        if (!previewInfoFromFetch || (!previewInfoFromFetch.previewUrl && !previewInfoFromFetch.fileUrl)) {
            console.warn("Preview cannot be opened: Missing necessary URLs for file", file.file_name);
            return;
        }

        const title = file.file_name;
        const fileType = file.file_type;
        let newState: Partial<PreviewModalState> | null = null;

        if (isImage(fileType) && previewInfoFromFetch.fileUrl) {
            newState = { isOpen: true, title, type: 'image', src: previewInfoFromFetch.fileUrl };
        } else if (previewInfoFromFetch.previewUrl) {
            newState = { isOpen: true, title, type: 'iframe', src: previewInfoFromFetch.previewUrl };
        }

        if (newState) {
            setPreviewModalState(prev => ({ ...prev, ...newState }));
        } else {
            console.warn("No suitable preview method found for file:", file.file_name);
        }
    }, [isImage]);


    const handleClosePreview = useCallback(() => {
        setPreviewModalState(prev => ({ ...prev, isOpen: false }));
        setTimeout(() => {
            setPreviewModalState({ isOpen: false, title: null, type: null, src: null });
        }, 300);
    }, []);

    const handleTileClick = useCallback(async (event: React.MouseEvent, file: FileDetail) => {
        if ((event.target as HTMLElement).closest('[data-download-button="true"]')) {
            return;
        }

        const fileId = file.file_id;
        let currentGlobalPreviewInfo = filePreviewData[fileId];

        if (currentGlobalPreviewInfo?.isLoading) {
            console.log("File info is already loading for:", file.file_name);
            return;
        }

        let urlsToUseForPreview: Pick<FilePreviewInfo, 'fileUrl' | 'previewUrl'> | null = null;

        if (!currentGlobalPreviewInfo || (!currentGlobalPreviewInfo.fileUrl && !currentGlobalPreviewInfo.previewUrl) || currentGlobalPreviewInfo.error) {
            try {
                urlsToUseForPreview = await fetchPreviewUrlForFile(file);
                currentGlobalPreviewInfo = filePreviewData[fileId] || { isLoading: false };
            } catch (error) {
                console.error("Failed to fetch preview URLs on tile click:", error);
            }
        } else {
            urlsToUseForPreview = { fileUrl: currentGlobalPreviewInfo.fileUrl, previewUrl: currentGlobalPreviewInfo.previewUrl };
        }

        const canPreview = urlsToUseForPreview &&
            !currentGlobalPreviewInfo?.isLoading && !currentGlobalPreviewInfo?.error &&
            (urlsToUseForPreview.previewUrl || (isImage(file.file_type) && urlsToUseForPreview.fileUrl));

        if (canPreview && urlsToUseForPreview) {
            handleOpenPreview(file, urlsToUseForPreview);
        } else if (!currentGlobalPreviewInfo?.isLoading) {
            console.warn("Preview not available for this file type or URL missing after attempted fetch.", file.file_name, "Details:", { urlsToUseForPreview, currentGlobalPreviewInfo });
        }
    }, [filePreviewData, fetchPreviewUrlForFile, handleOpenPreview, isImage]);


    const handleDownloadAttempt = useCallback(async (event: React.MouseEvent, file: FileDetail) => {
        const fileId = file.file_id;
        let currentGlobalPreviewInfo = filePreviewData[fileId];
        let fileUrlToDownload = currentGlobalPreviewInfo?.fileUrl;

        if (currentGlobalPreviewInfo?.isLoading) {
            event.preventDefault();
            console.log("File info is loading, download deferred for:", file.file_name);
            return;
        }

        if (!fileUrlToDownload || currentGlobalPreviewInfo.error) {
            event.preventDefault();
            try {
                const fetchedUrls = await fetchPreviewUrlForFile(file);
                if (fetchedUrls?.fileUrl) {
                    fileUrlToDownload = fetchedUrls.fileUrl;
                    const link = document.createElement('a');
                    link.href = fileUrlToDownload!;
                    link.setAttribute('download', file.file_name);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                } else if (!filePreviewData[fileId]?.error) {
                    console.warn(`Download URL could not be obtained for ${file.file_name}. API might not have returned a file_url.`);
                }
            } catch (error) {
                console.error(`Failed to fetch download URL for ${file.file_name}:`, error);
            }
        }
    }, [filePreviewData, fetchPreviewUrlForFile]);


    const shouldRender = message || (who === 'user' && fileDetails && fileDetails.length > 0);

    return (
        <AnimatePresence>
            {shouldRender && (
                <motion.div
                    key={messageId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className={`clear-both flex py-2 justify-center`}
                >
                    <ChatLineContainer who={who} elevation={1} sx={{
                        width: "100%",
                        border: `1px solid ${theme.palette.divider}`
                    }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Avatar sx={{
                                width: 32,
                                height: 32,
                                mr: 2,
                                bgcolor: who === 'bot' ? 'white' : theme.palette.secondary.main
                            }}>
                                {who === 'bot' ? (
                                    <img
                                        src={
                                            datalinker_sm_logo
                                        }
                                        alt="Logo"
                                        style={{ maxHeight: "30px" }}
                                    />
                                ) : (
                                    <FaUser style={{ fontSize: '1rem' }} />
                                )}
                            </Avatar>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.text.primary
                                }}
                            >
                                {who === 'bot' ? 'DatalinkerAI' : username}
                            </Typography>
                        </Box>

                        {message && (
                            <Box position="relative" mb={fileDetails && fileDetails.length > 0 ? 2 : 0} sx={{ wordBreak: 'break-word' }}>
                                <Markdown markdown={message} />
                                {who === 'bot' && loading && isLastMessage && <LoadingChatLine />}
                                {who === 'bot' && !loading && message.length > 10 && (
                                    <Box sx={{ position: 'absolute', top: {xs: -40, sm: -50}, right: {xs: 0, sm: -8}, display: 'flex', bgcolor: alpha(theme.palette.background.default, 0.7), backdropFilter: 'blur(3px)', borderRadius: '50px', boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`, p: 0.5, border: `1px solid ${theme.palette.divider}` }}>
                                        <Tooltip title={isCopied ? 'Copied!' : 'Copy Text'} arrow placement="top">
                                            <span><IconButton size="small" onClick={copyToClipboard} sx={{ mr: 0.5 }}>{isCopied ? <FaCheck fontSize="inherit" color={theme.palette.success.main} /> : <FaCopy fontSize="small" />}</IconButton></span>
                                        </Tooltip>
                                        <Tooltip title={isTTSPlaying ? 'Stop Narration' : 'Narrate Text'} arrow placement="top">
                                            <span><IconButton size="small" onClick={isTTSPlaying ? stopAudio : playAudio} disabled={isTTSLoading}>{isTTSLoading ? <CircularProgress size={16} thickness={5}/> : isTTSPlaying ? <FaStop fontSize="small" /> : <FaPlay fontSize="small" />}</IconButton></span>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {who === 'user' && fileDetails && fileDetails.length > 0 && (
                            <Box mt={message ? 1.5 : 0} pt={message ? 1.5 : 0} borderTop={message ? `1px solid ${theme.palette.divider}`: 'none'}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                    Attached:
                                </Typography>
                                <Grid container spacing={1}>
                                    {fileDetails.map((file: FileDetail) => {
                                        const currentPreviewInfo = filePreviewData[file.file_id];
                                        const isLoading = currentPreviewInfo?.isLoading ?? false;
                                        const fetchError = currentPreviewInfo?.error;

                                        const canOpenPreview = !isLoading && !fetchError &&
                                            (currentPreviewInfo?.previewUrl || (isImage(file.file_type) && currentPreviewInfo?.fileUrl));

                                        const imageThumbSource = isImage(file.file_type) ? (currentPreviewInfo?.fileUrl || getLocalApiFileUrl(file)) : undefined;

                                        const hasValidDownloadUrl = !isLoading && !fetchError && !!currentPreviewInfo?.fileUrl;
                                        const downloadButtonIsActuallyDisabled = isLoading; // Only truly disable if actively loading

                                        const tileCursor = isLoading ? 'default' : (canOpenPreview || !fetchError) ? 'pointer' : 'default';

                                        return (
                                            <Grid item key={file.file_id} xs={12} sm={6} md={6} lg={4}>
                                                <Tooltip
                                                    title={
                                                        isLoading ? "Loading file info..." :
                                                            fetchError ? `Error: ${fetchError}` :
                                                                canOpenPreview ? `Click to preview ${file.file_name}` :
                                                                    `File: ${file.file_name} (Preview not available or requires fetch)`
                                                    }
                                                    arrow
                                                    placement="top-start"
                                                >
                                                    <Paper
                                                        data-fileid={file.file_id}
                                                        variant="outlined"
                                                        onClick={(e) => handleTileClick(e, file)}
                                                        sx={{
                                                            display: 'flex', alignItems: 'center', p: 1, height: '56px', overflow: 'hidden', position: 'relative',
                                                            cursor: tileCursor,
                                                            backgroundColor: alpha(theme.palette.background.paper, 0.6),
                                                            borderColor: fetchError ? theme.palette.error.light : theme.palette.divider,
                                                            transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                                                            '&:hover': {
                                                                backgroundColor: (tileCursor === 'pointer') ? alpha(theme.palette.action.hover, 0.4) : alpha(theme.palette.background.paper, 0.6),
                                                                borderColor: (tileCursor === 'pointer') ? theme.palette.primary.light : (fetchError ? theme.palette.error.light : theme.palette.divider),
                                                                boxShadow: (tileCursor === 'pointer') ? `0 1px 4px ${alpha(theme.palette.common.black, 0.1)}` : 'none',
                                                            }
                                                        }}
                                                    >
                                                        {(isLoading || fetchError) && ( <Box sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, display: 'flex', alignItems: 'center' }}> {isLoading && <CircularProgress size={20} thickness={5} />} {fetchError && !isLoading && <Tooltip title={fetchError} arrow><ErrorOutlineIcon color="error" fontSize="small" /></Tooltip>} </Box> )}
                                                        <Box sx={{ flexShrink: 0, width: 36, height: 36, mr: 1.5, ml: (isLoading || fetchError) ? 3.5 : 0, /* Increased margin when loader/error shown */ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, overflow: 'hidden', bgcolor: alpha(theme.palette.action.selected, 0.1), opacity: isLoading ? 0.4 : 1, transition: 'opacity 0.2s, margin-left 0.2s', position: 'relative' }}>
                                                            {imageThumbSource ? ( <img src={imageThumbSource} alt={`${file.file_name} thumbnail`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { const target = e.currentTarget; target.style.display = 'none'; const parent = target.parentElement; if (parent && !parent.querySelector('.placeholder-icon')) { const placeholder = document.createElement('span'); placeholder.className = 'placeholder-icon'; placeholder.style.cssText = 'display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;'; placeholder.innerHTML = ReactDOMServer.renderToString(<ImageOutlinedIcon fontSize="medium" color="action" />); parent.appendChild(placeholder); } }}/> )
                                                                : ( getIconComponent(file.file_type, 'medium') )}
                                                        </Box>
                                                        <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 4.5, opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.2s', }}> {file.file_name} </Typography>
                                                        <Box sx={{ position: 'absolute', top: '50%', right: 6, transform: 'translateY(-50%)', opacity: !isLoading || hasValidDownloadUrl ? 1 : 0.3, visibility: 'visible', transition: 'opacity 0.2s, visibility 0.2s' }} data-download-button="true" >
                                                            <Tooltip title={hasValidDownloadUrl ? "Download file" : (isLoading ? "Loading..." : (fetchError ? `Error: ${fetchError}`: "Download file (will fetch URL)"))} arrow>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e: any) => handleDownloadAttempt(e, file)}
                                                                        disabled={downloadButtonIsActuallyDisabled}
                                                                        href={hasValidDownloadUrl ? currentPreviewInfo!.fileUrl! : undefined}
                                                                        download={hasValidDownloadUrl ? file.file_name : undefined}
                                                                        target="_blank" // Open in new tab for direct downloads
                                                                        rel="noopener noreferrer"
                                                                        component={hasValidDownloadUrl ? 'a' : 'button'} // Dynamically switch based on URL availability
                                                                        aria-label={`Download ${file.file_name}`}
                                                                        sx={{
                                                                            bgcolor: alpha(theme.palette.action.active, 0.05),
                                                                            '&:hover': { bgcolor: alpha(theme.palette.action.active, 0.1) },
                                                                        }}
                                                                    >
                                                                        <DownloadIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </Box>
                                                    </Paper>
                                                </Tooltip>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        )}

                    </ChatLineContainer>
                    <audio ref={audioRef} style={{ display: 'none' }} preload="none" />
                </motion.div>
            )}

            <Modal
                open={previewModalState.isOpen}
                onClose={handleClosePreview}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 500, style: { backgroundColor: alpha(theme.palette.common.black, 0.6) } } }}
                aria-labelledby="file-preview-modal-title"
                aria-describedby="file-preview-modal-description"
            >
                <Fade in={previewModalState.isOpen} timeout={500}>
                    <Box sx={modalStyle}>
                        <Box sx={modalHeaderStyle}>
                            <Typography id="file-preview-modal-title" variant="h6" component="h2" noWrap sx={{ maxWidth: 'calc(100% - 50px)', fontSize: '1.1rem' }}>
                                {previewModalState.title || 'File Preview'}
                            </Typography>
                            <Tooltip title="Close Preview" arrow>
                                <IconButton onClick={handleClosePreview} size="medium" aria-label="Close preview"><CloseIcon /></IconButton>
                            </Tooltip>
                        </Box>
                        <Box id="file-preview-modal-description" sx={modalContentStyle}>
                            {previewModalState.src ? (
                                previewModalState.type === 'image' ? (
                                    <Box sx={imagePreviewWrapperStyle}>
                                        <img
                                            src={previewModalState.src}
                                            alt={previewModalState.title || 'Image Preview'}
                                            style={imagePreviewStyle}
                                        />
                                    </Box>
                                ) : previewModalState.type === 'iframe' ? (
                                    <iframe
                                        src={previewModalState.src}
                                        style={iframeStyle}
                                        title={previewModalState.title || 'File Preview Content'}
                                        sandbox="allow-scripts allow-same-origin allow-popups"
                                    />
                                ) : (
                                    <Typography sx={{p:2}}>Cannot determine preview type.</Typography>
                                )
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
                            )}
                        </Box>
                    </Box>
                </Fade>
            </Modal>

        </AnimatePresence>
    );
}